// Binary read/write primitives for the wire codec (NET-1). Isomorphic: uses only
// Uint8Array / DataView / TextEncoder-Decoder, all present in Node and the browser,
// so the same encode/decode runs on the server and in the client replica. No zod
// here — this is the byte layer; protocol.ts layers message framing + validation on
// top. All integers are little-endian; lengths and ids use unsigned LEB128 varints
// so small values stay one byte.

const UTF8_ENC = new TextEncoder();
const UTF8_DEC = new TextDecoder();

/** Growable little-endian writer. `finish()` returns exactly the bytes written. */
export class ByteWriter {
  private buf: Uint8Array;
  private view: DataView;
  private pos = 0;

  constructor(initial = 64) {
    this.buf = new Uint8Array(initial);
    this.view = new DataView(this.buf.buffer);
  }

  private ensure(n: number): void {
    if (this.pos + n <= this.buf.length) return;
    let cap = this.buf.length * 2;
    while (cap < this.pos + n) cap *= 2;
    const next = new Uint8Array(cap);
    next.set(this.buf);
    this.buf = next;
    this.view = new DataView(this.buf.buffer);
  }

  u8(v: number): this {
    this.ensure(1);
    this.buf[this.pos++] = v & 0xff;
    return this;
  }

  bool(v: boolean): this {
    return this.u8(v ? 1 : 0);
  }

  u16(v: number): this {
    this.ensure(2);
    this.view.setUint16(this.pos, v & 0xffff, true);
    this.pos += 2;
    return this;
  }

  u32(v: number): this {
    this.ensure(4);
    this.view.setUint32(this.pos, v >>> 0, true);
    this.pos += 4;
    return this;
  }

  f64(v: number): this {
    this.ensure(8);
    this.view.setFloat64(this.pos, v, true);
    this.pos += 8;
    return this;
  }

  /** Unsigned LEB128 varint (non-negative safe integers only). */
  varint(v: number): this {
    if (v < 0 || !Number.isSafeInteger(v)) throw new RangeError(`varint out of range: ${v}`);
    let n = v;
    while (n >= 0x80) {
      this.u8((n & 0x7f) | 0x80);
      n = Math.floor(n / 128);
    }
    this.u8(n);
    return this;
  }

  /** Signed zig-zag varint. */
  svarint(v: number): this {
    const zig = v >= 0 ? v * 2 : -v * 2 - 1;
    return this.varint(zig);
  }

  str(s: string): this {
    const bytes = UTF8_ENC.encode(s);
    this.varint(bytes.length);
    this.ensure(bytes.length);
    this.buf.set(bytes, this.pos);
    this.pos += bytes.length;
    return this;
  }

  /** Raw byte blob, length-prefixed. */
  bytes(b: Uint8Array): this {
    this.varint(b.length);
    this.ensure(b.length);
    this.buf.set(b, this.pos);
    this.pos += b.length;
    return this;
  }

  /** u16 array, varint-count-prefixed (section palettes / block ids). */
  u16array(a: ArrayLike<number>): this {
    this.varint(a.length);
    for (let i = 0; i < a.length; i++) this.u16(a[i]!);
    return this;
  }

  finish(): Uint8Array {
    return this.buf.slice(0, this.pos);
  }

  get length(): number {
    return this.pos;
  }
}

/** Little-endian reader over a fixed byte range. Throws past the end. */
export class ByteReader {
  private view: DataView;
  private pos = 0;

  constructor(private readonly buf: Uint8Array) {
    this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  }

  private need(n: number): void {
    if (this.pos + n > this.buf.length) throw new RangeError('unexpected end of buffer');
  }

  u8(): number {
    this.need(1);
    return this.buf[this.pos++]!;
  }

  bool(): boolean {
    return this.u8() !== 0;
  }

  u16(): number {
    this.need(2);
    const v = this.view.getUint16(this.pos, true);
    this.pos += 2;
    return v;
  }

  u32(): number {
    this.need(4);
    const v = this.view.getUint32(this.pos, true);
    this.pos += 4;
    return v;
  }

  f64(): number {
    this.need(8);
    const v = this.view.getFloat64(this.pos, true);
    this.pos += 8;
    return v;
  }

  varint(): number {
    let result = 0;
    let shift = 1;
    for (;;) {
      const byte = this.u8();
      result += (byte & 0x7f) * shift;
      if ((byte & 0x80) === 0) break;
      shift *= 128;
      if (shift > Number.MAX_SAFE_INTEGER) throw new RangeError('varint too large');
    }
    return result;
  }

  svarint(): number {
    const zig = this.varint();
    return zig % 2 === 0 ? zig / 2 : -(zig + 1) / 2;
  }

  str(): string {
    const len = this.varint();
    this.need(len);
    const s = UTF8_DEC.decode(this.buf.subarray(this.pos, this.pos + len));
    this.pos += len;
    return s;
  }

  bytes(): Uint8Array {
    const len = this.varint();
    this.need(len);
    const b = this.buf.slice(this.pos, this.pos + len);
    this.pos += len;
    return b;
  }

  u16array(): number[] {
    const len = this.varint();
    const out = new Array<number>(len);
    for (let i = 0; i < len; i++) out[i] = this.u16();
    return out;
  }

  /** Bytes consumed so far — the framing layer checks a message left none behind. */
  get offset(): number {
    return this.pos;
  }

  get remaining(): number {
    return this.buf.length - this.pos;
  }
}
